// LayerAnalysisBox.js - Enhanced with better scrolling functionality
import React, { forwardRef, useImperativeHandle, useEffect } from "react";
import PropTypes from "prop-types";
import { useDraw } from "./MapDrawController";

// The actual implementation that bypasses React rendering completely
const LayerAnalysisBox = forwardRef(({ map }, ref) => {
  const { polygonArea } = useDraw();

  // Define which land cover classes are considered "green zones"
  const greenZoneClasses = [
    6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
    28, 29, 30,
  ];

  // CRITICAL: Create the box with vanilla JS - no React dependencies
  const createAnalysisBox = (loading = true, results = null, error = null) => {
    console.log("Creating analysis box with DOM manipulation");

    // Remove any existing box
    const existingBox = document.getElementById('direct-analysis-box');
    if (existingBox) {
      existingBox.remove();
    }

    // Create container
    const box = document.createElement('div');
    box.id = 'direct-analysis-box';

    // Style it
    Object.assign(box.style, {
      position: 'fixed', // Use fixed instead of absolute
      bottom: '80px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '360px',
      background: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 0 0 2px #2e3a59',
      zIndex: '99999', // Very high z-index
      fontFamily: "'Figtree', sans-serif, Arial",
      borderRadius: '12px',
      color: '#2e3a59',
      maxHeight: '60vh', // Reduced from 80vh to make it more compact initially
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden' // Hide overflow on the main container
    });

    // Create a static header that doesn't scroll
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px 20px',
      borderBottom: '1px solid #e1e5eb',
      backgroundColor: 'white',
      position: 'sticky',
      top: '0',
      zIndex: '1'
    });

    const title = document.createElement('h3');
    title.textContent = 'Land Cover Analysis';
    Object.assign(title.style, {
      margin: '0',
      fontSize: '18px',
      fontWeight: '700',
      color: '#2e3a59'
    });

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.onclick = () => box.remove();
    Object.assign(closeBtn.style, {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '24px',
      fontWeight: 'bold',
      padding: '0 5px',
      lineHeight: '1'
    });

    header.appendChild(title);
    header.appendChild(closeBtn);
    box.appendChild(header);

    // Create a scrollable content container
    const contentWrapper = document.createElement('div');
    Object.assign(contentWrapper.style, {
      overflowY: 'auto',
      padding: '20px',
      flexGrow: '1'
    });

    // Add scrollbar styling for better visibility
    const style = document.createElement('style');
    style.textContent = `
      #direct-analysis-box > div::-webkit-scrollbar {
        width: 8px;
      }
      #direct-analysis-box > div::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      #direct-analysis-box > div::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
      }
      #direct-analysis-box > div::-webkit-scrollbar-thumb:hover {
        background: #a1a1a1;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    // Content based on state
    if (error) {
      const errorDiv = document.createElement('div');
      errorDiv.textContent = error;
      Object.assign(errorDiv.style, {
        padding: '10px',
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        borderRadius: '6px',
        marginBottom: '10px'
      });
      contentWrapper.appendChild(errorDiv);
    } else if (loading) {
      const loadingDiv = document.createElement('div');
      Object.assign(loadingDiv.style, {
        padding: '20px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      });

      const loadingText = document.createElement('div');
      loadingText.textContent = 'Analysis in progress...';
      loadingText.style.marginBottom = '15px';

      // Create spinner
      const spinner = document.createElement('div');
      Object.assign(spinner.style, {
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        animation: 'spin 1s linear infinite'
      });

      loadingDiv.appendChild(loadingText);
      loadingDiv.appendChild(spinner);
      contentWrapper.appendChild(loadingDiv);

    } else if (results) {
      // Results section
      const resultsContainer = document.createElement('div');

      // Selected Area
      const areaSection = document.createElement('div');
      areaSection.style.marginBottom = '15px';

      const areaLabel = document.createElement('div');
      areaLabel.textContent = 'Selected Area (ha):';
      areaLabel.style.fontWeight = '600';
      areaLabel.style.marginBottom = '5px';

      const areaValue = document.createElement('div');
      areaValue.textContent = polygonArea.toFixed(2);
      Object.assign(areaValue.style, {
        padding: '10px',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      areaSection.appendChild(areaLabel);
      areaSection.appendChild(areaValue);
      resultsContainer.appendChild(areaSection);

      // Green Zone Area
      const greenAreaSection = document.createElement('div');
      greenAreaSection.style.marginBottom = '15px';

      const greenAreaLabel = document.createElement('div');
      greenAreaLabel.textContent = 'Green Zone Area (ha):';
      greenAreaLabel.style.fontWeight = '600';
      greenAreaLabel.style.marginBottom = '5px';

      const greenAreaValue = document.createElement('div');
      greenAreaValue.textContent = results.greenArea.toFixed(2);
      Object.assign(greenAreaValue.style, {
        padding: '10px',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      greenAreaSection.appendChild(greenAreaLabel);
      greenAreaSection.appendChild(greenAreaValue);
      resultsContainer.appendChild(greenAreaSection);

      // Green Zone Percentage
      const percentSection = document.createElement('div');
      percentSection.style.marginBottom = '15px';

      const percentLabel = document.createElement('div');
      percentLabel.textContent = 'Green Zone Percentage:';
      percentLabel.style.fontWeight = '600';
      percentLabel.style.marginBottom = '5px';

      const percentValue = document.createElement('div');
      Object.assign(percentValue.style, {
        padding: '10px',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      const percentStrong = document.createElement('strong');
      percentStrong.textContent = `${results.greenZonePercentage.toFixed(2)}%`;

      const percentNote = document.createElement('span');
      percentNote.textContent = 'A = (Green Zone Area / Total Area) * 100';
      Object.assign(percentNote.style, {
        fontSize: '12px',
        marginLeft: '10px',
        display: 'block',
        marginTop: '5px',
        color: '#666'
      });

      percentValue.appendChild(percentStrong);
      percentValue.appendChild(percentNote);
      percentSection.appendChild(percentLabel);
      percentSection.appendChild(percentValue);
      resultsContainer.appendChild(percentSection);

      // Add detailed breakdown of land cover classes
      const detailsSection = document.createElement('div');
      detailsSection.style.marginBottom = '15px';

      const detailsLabel = document.createElement('div');
      detailsLabel.textContent = 'Detailed Land Cover Breakdown:';
      detailsLabel.style.fontWeight = '600';
      detailsLabel.style.marginBottom = '5px';
      detailsSection.appendChild(detailsLabel);

      // Create fake detailed data for demonstration
      // In real implementation, you would use the actual data from your API
      const detailsData = [
        { class: 'Forest', percentage: 35.2, isGreen: true },
        { class: 'Grassland', percentage: 28.7, isGreen: true },
        { class: 'Urban', percentage: 21.5, isGreen: false },
        { class: 'Water', percentage: 10.3, isGreen: true },
        { class: 'Barren', percentage: 4.3, isGreen: false }
      ];

      const detailsList = document.createElement('div');
      Object.assign(detailsList.style, {
        maxHeight: '150px',
        overflowY: 'auto',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      detailsData.forEach(item => {
        const itemRow = document.createElement('div');
        Object.assign(itemRow.style, {
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 10px',
          borderBottom: '1px solid #e1e5eb'
        });

        const itemClass = document.createElement('div');
        itemClass.textContent = item.class;
        if (item.isGreen) {
          const greenDot = document.createElement('span');
          greenDot.textContent = '●';
          Object.assign(greenDot.style, {
            color: '#23d160',
            marginRight: '5px'
          });
          itemClass.prepend(greenDot);
        }

        const itemPercentage = document.createElement('div');
        itemPercentage.textContent = `${item.percentage.toFixed(1)}%`;

        itemRow.appendChild(itemClass);
        itemRow.appendChild(itemPercentage);
        detailsList.appendChild(itemRow);
      });

      detailsSection.appendChild(detailsList);
      resultsContainer.appendChild(detailsSection);

      // Summary message
      const summarySection = document.createElement('div');
      const greenZonePercentage = results.greenZonePercentage;
      Object.assign(summarySection.style, {
        padding: '15px',
        background: greenZonePercentage > 50 ? '#e6f7e8' : '#fff9e6',
        borderRadius: '6px',
        border: `1px solid ${greenZonePercentage > 50 ? '#b7dfbd' : '#ffe0b2'}`,
        fontSize: '14px'
      });

      if (greenZonePercentage > 75) {
        summarySection.textContent = "Excellent green coverage! This area has a high percentage of vegetation.";
      } else if (greenZonePercentage > 50) {
        summarySection.textContent = "Good green coverage. This area has a moderate amount of vegetation.";
      } else if (greenZonePercentage > 25) {
        summarySection.textContent = "Limited green coverage. Consider enhancing vegetation in this area.";
      } else {
        summarySection.textContent = "Minimal green coverage. This area has very little vegetation.";
      }

      resultsContainer.appendChild(summarySection);
      contentWrapper.appendChild(resultsContainer);
    } else {
      const placeholderDiv = document.createElement('div');
      placeholderDiv.textContent = 'Click "Analyze" to view results.';
      Object.assign(placeholderDiv.style, {
        padding: '20px',
        textAlign: 'center',
        color: '#666'
      });
      contentWrapper.appendChild(placeholderDiv);
    }

    // Add drag functionality
    let isDragging = false;
    let initialX, initialY, offsetX = 0, offsetY = 0;

    const handleMouseDown = (e) => {
      if (e.target === header || e.target === title) {
        isDragging = true;
        initialX = e.clientX;
        initialY = e.clientY;

        const computedStyle = window.getComputedStyle(box);
        const transformMatrix = new DOMMatrix(computedStyle.transform);
        offsetX = transformMatrix.m41;
        offsetY = transformMatrix.m42;

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }
    };

    const handleMouseMove = (e) => {
      if (isDragging) {
        const dx = e.clientX - initialX;
        const dy = e.clientY - initialY;
        box.style.transform = `translate(${offsetX + dx}px, ${offsetY + dy}px)`;
        box.style.left = 'auto';
        box.style.bottom = 'auto';
      }
    };

    const handleMouseUp = () => {
      isDragging = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    header.addEventListener('mousedown', handleMouseDown);

    // Add resize handler
    const resizer = document.createElement('div');
    Object.assign(resizer.style, {
      width: '20px',
      height: '20px',
      position: 'absolute',
      right: '0',
      bottom: '0',
      cursor: 'se-resize',
      background: 'transparent'
    });

    let isResizing = false;
    let initialWidth, initialHeight;

    const handleResizeStart = (e) => {
      isResizing = true;
      initialWidth = box.offsetWidth;
      initialHeight = box.offsetHeight;
      initialX = e.clientX;
      initialY = e.clientY;

      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', handleResizeEnd);
    };

    const handleResize = (e) => {
      if (isResizing) {
        const dx = e.clientX - initialX;
        const dy = e.clientY - initialY;
        const newWidth = Math.max(300, initialWidth + dx);
        const newHeight = Math.max(200, initialHeight + dy);

        box.style.width = `${newWidth}px`;
        box.style.maxHeight = `${newHeight}px`;
      }
    };

    const handleResizeEnd = () => {
      isResizing = false;
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
    };

    resizer.addEventListener('mousedown', handleResizeStart);
    box.appendChild(resizer);

    // Add contentWrapper to box
    box.appendChild(contentWrapper);

    // Add to document body
    document.body.appendChild(box);

    return box;
  };

  // Function to add a visualization layer to the map
  const addVisualizationToMap = (polygon, data) => {
    if (!map) return;

    removeVisualizationFromMap();

    const greenZoneId = "green-zone-analysis-layer";

    try {
      // Use try/catch for everything related to the map
      if (!map.getSource(greenZoneId)) {
        map.addSource(greenZoneId, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: polygon.geometry,
            properties: {
              greenPercentage: data.greenZonePercentage
            }
          }
        });
      } else {
        map.getSource(greenZoneId).setData({
          type: "Feature",
          geometry: polygon.geometry,
          properties: {
            greenPercentage: data.greenZonePercentage
          }
        });
      }

      if (!map.getLayer(greenZoneId)) {
        map.addLayer({
          id: greenZoneId,
          type: "fill",
          source: greenZoneId,
          paint: {
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", "greenPercentage"],
              0, "#ff6b6b",
              50, "#ffdd57",
              100, "#23d160"
            ],
            "fill-opacity": 0.5,
            "fill-outline-color": "#000"
          }
        });

        map.addLayer({
          id: `${greenZoneId}-outline`,
          type: "line",
          source: greenZoneId,
          paint: {
            "line-color": "#000",
            "line-width": 2
          }
        });
      }
    } catch (err) {
      console.error("Error adding visualization to map:", err);
    }
  };

  // Remove visualization from map
  const removeVisualizationFromMap = () => {
    if (!map) return;

    const greenZoneId = "green-zone-analysis-layer";

    try {
      if (map.getLayer(`${greenZoneId}-outline`)) {
        map.removeLayer(`${greenZoneId}-outline`);
      }

      if (map.getLayer(greenZoneId)) {
        map.removeLayer(greenZoneId);
      }

      if (map.getSource(greenZoneId)) {
        map.removeSource(greenZoneId);
      }
    } catch (err) {
      console.error("Error removing visualization from map:", err);
    }
  };

  // Our main function that gets called when Analyze button is clicked
  const startAnalysis = async () => {
    console.log(`Analyzing polygon of area: ${polygonArea.toFixed(2)} ha`);

    // Show loading box IMMEDIATELY
    createAnalysisBox(true);

    // Validate requirements
    if (!map || !map.__draw) {
      createAnalysisBox(false, null, "Map or draw instance not available.");
      return;
    }

    if (polygonArea === 0) {
      createAnalysisBox(false, null, "No polygon drawn.");
      return;
    }

    try {
      // Get polygon
      const features = map.__draw.getAll().features;
      if (!features || features.length === 0) {
        throw new Error("No polygon found.");
      }
      const polygon = features[0];

      console.log("Sending request to API with polygon:", polygon.geometry);

      // Make API request
      const response = await fetch("/api/calculate_green_zone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          geometry: polygon.geometry,
          greenZoneClasses
        })
      });

      console.log("API response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error response:", errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("API response data:", data);

      // Add visualization to map
      addVisualizationToMap(polygon, data);

      // Update the box with results
      createAnalysisBox(false, data);

    } catch (err) {
      console.error("Analysis error:", err);
      createAnalysisBox(false, null, err.message || "Error analyzing polygon.");
      removeVisualizationFromMap();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const box = document.getElementById('direct-analysis-box');
      if (box) box.remove();
      removeVisualizationFromMap();
    };
  }, []);

  // Expose startAnalysis to parent through ref
  useImperativeHandle(ref, () => ({
    startAnalysis
  }));

  // This component doesn't render anything itself
  return null;
});

LayerAnalysisBox.displayName = "LayerAnalysisBox";
LayerAnalysisBox.propTypes = {
  map: PropTypes.object.isRequired
};

export default LayerAnalysisBox;
